import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { Copy, Check } from 'lucide-react';
import './ShareButton.css';
const ShareButton = ({ url, title, description = '' }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [copied, setCopied] = useState(false);
    const [settings, setSettings] = useState({
        enabledPlatforms: {
            twitter: true,
            facebook: true,
            linkedin: true,
            whatsapp: true,
            email: true,
            copy: true,
        }
    });
    const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';
    useEffect(() => {
        // Fetch share button settings from API
        const fetchSettings = async () => {
            try {
                const response = await fetch(`${API_URL}/settings`);
                if (response.ok) {
                    const data = await response.json();
                    if (data.shareSettings) {
                        setSettings(JSON.parse(data.shareSettings));
                    }
                }
            }
            catch (error) {
                console.error('Failed to fetch share settings:', error);
            }
        };
        fetchSettings();
    }, []);
    const toggleMenu = () => {
        setIsOpen(!isOpen);
    };
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            toggleMenu();
        }
    };
    const copyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
        catch (err) {
            console.error('Failed to copy:', err);
        }
    };
    const shareLinks = {
        twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
        linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
        whatsapp: `https://wa.me/?text=${encodeURIComponent(title + ' ' + url)}`,
        email: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(description + '\n\n' + url)}`,
    };
    const platforms = [
        {
            name: 'twitter',
            icon: (_jsx("svg", { fill: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { d: "M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" }) })),
            label: 'Twitter',
            className: 'twitter',
            enabled: settings.enabledPlatforms.twitter,
        },
        {
            name: 'facebook',
            icon: (_jsx("svg", { fill: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { d: "M9.101 23.691v-7.98H6.627v-3.667h2.474v-1.58c0-4.085 1.848-5.978 5.858-5.978.401 0 .955.042 1.468.103a8.68 8.68 0 0 1 1.141.195v3.325a8.623 8.623 0 0 0-.653-.036 26.805 26.805 0 0 0-.733-.009c-.707 0-1.259.096-1.675.309a1.686 1.686 0 0 0-.679.622c-.258.42-.374.995-.374 1.752v1.297h3.919l-.386 3.667h-3.533v7.98H9.101z" }) })),
            label: 'Facebook',
            className: 'facebook',
            enabled: settings.enabledPlatforms.facebook,
        },
        {
            name: 'linkedin',
            icon: (_jsx("svg", { fill: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { d: "M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" }) })),
            label: 'LinkedIn',
            className: 'linkedin',
            enabled: settings.enabledPlatforms.linkedin,
        },
        {
            name: 'whatsapp',
            icon: (_jsx("svg", { fill: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { d: "M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" }) })),
            label: 'WhatsApp',
            className: 'whatsapp',
            enabled: settings.enabledPlatforms.whatsapp,
        },
        {
            name: 'email',
            icon: (_jsx("svg", { fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" }) })),
            label: 'Email',
            className: 'email',
            enabled: settings.enabledPlatforms.email,
        },
    ];
    const enabledPlatforms = platforms.filter(p => p.enabled);
    const totalPlatforms = settings.enabledPlatforms.copy ? enabledPlatforms.length + 1 : enabledPlatforms.length;
    return (_jsxs(_Fragment, { children: [isOpen && _jsx("div", { className: "share-backdrop", onClick: () => setIsOpen(false) }), _jsx("div", { className: "share-button-wrapper", children: _jsxs("nav", { className: `share-nav ${isOpen ? 'clicked' : 'closed'}`, tabIndex: 0, onClick: toggleMenu, onKeyDown: handleKeyDown, role: "button", "aria-label": "Share menu", "aria-expanded": isOpen, children: [_jsx("span", { className: "share-nav-text", children: "Share" }), enabledPlatforms.map((platform, index) => {
                            const angle = ((index - 1) * (360 / totalPlatforms));
                            const style = {
                                transform: isOpen
                                    ? `rotate(${angle}deg) translateX(5rem) rotate(-${angle}deg)`
                                    : 'rotate(0deg)',
                            };
                            return (_jsx("a", { href: shareLinks[platform.name], target: "_blank", rel: "noopener noreferrer", className: `ss-icon ${platform.className}`, style: style, "aria-label": `Share on ${platform.label}`, onClick: (e) => e.stopPropagation(), children: platform.icon }, platform.name));
                        }), settings.enabledPlatforms.copy && (_jsx("a", { href: "#", onClick: (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                copyToClipboard();
                            }, className: "ss-icon copy", style: {
                                transform: isOpen
                                    ? `rotate(${(enabledPlatforms.length - 1) * (360 / totalPlatforms)}deg) translateX(5rem) rotate(-${(enabledPlatforms.length - 1) * (360 / totalPlatforms)}deg)`
                                    : 'rotate(0deg)',
                            }, "aria-label": "Copy link", children: copied ? _jsx(Check, {}) : _jsx(Copy, {}) }))] }) })] }));
};
export default ShareButton;
